/**
 * Enhanced logger utility for the frontend
 * Provides structured logging with module context
 * Logs are disabled in production by default
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

/**
 * Session ID for correlating logs from same user session
 */
const sessionId = Math.random().toString(36).substring(2, 8);

class Logger {
  private level: LogLevel;
  private enabled: boolean;

  constructor() {
    // Disable logging in production by default (can be enabled via env var)
    this.enabled = process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ENABLE_LOGS === 'true';
    
    // Set log level based on environment
    const envLevel = process.env.REACT_APP_LOG_LEVEL?.toUpperCase();
    this.level = envLevel ? (LogLevel[envLevel as keyof typeof LogLevel] ?? LogLevel.INFO) : LogLevel.INFO;
  }

  private shouldLog(level: LogLevel): boolean {
    return this.enabled && level <= this.level;
  }

  private getTimestamp(): string {
    return new Date().toLocaleString('fr-FR', {
      timeZone: 'Europe/Paris',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  private formatMessage(level: string, module: string, message: string, data?: Record<string, unknown>): string {
    const timestamp = this.getTimestamp();
    const dataStr = data && Object.keys(data).length > 0 ? ` ${JSON.stringify(data)}` : '';
    return `[${timestamp}] [${level.padEnd(5)}] [${sessionId}] [${module}] ${message}${dataStr}`;
  }

  /**
   * Log an error message
   */
  error(module: string, message: string, data?: Record<string, unknown>): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage('ERROR', module, message, data));
    }
  }

  /**
   * Log a warning message
   */
  warn(module: string, message: string, data?: Record<string, unknown>): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage('WARN', module, message, data));
    }
  }

  /**
   * Log an info message
   */
  info(module: string, message: string, data?: Record<string, unknown>): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(this.formatMessage('INFO', module, message, data));
    }
  }

  /**
   * Log a debug message
   */
  debug(module: string, message: string, data?: Record<string, unknown>): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage('DEBUG', module, message, data));
    }
  }

  /**
   * Get session ID for correlation
   */
  getSessionId(): string {
    return sessionId;
  }
}

export const logger = new Logger();
